import {IFilterType} from './advanced-filter-types.interface';

//#region ENUM FilterTypesEnum
/**
 * Enum representing different types of filters that can be applied.
 *
 * @readonly
 * @enum {string}
 */
export enum FilterTypesEnum {
  equalTo = 'equalTo',
  greaterThan = 'greaterThan',
  greaterThanOrEqualTo = 'greaterThanOrEqualTo',
  isEmpty = 'isEmpty',
  isNotAnyOf = 'isNotAnyOf',
  isNotEmpty = 'isNotEmpty',
  isOneOf = 'isOneOf',
  lessThan = 'lessThan',
  lessThanOrEqualTo = 'lessThanOrEqualTo',
  notEqualTo = 'notEqualTo',
  partiallyEqualTo = 'partiallyEqualTo',
  contains = 'contains',
  doesNotContain = 'doesNotContain',
  startsWith = 'startsWith',
  endsWith = 'endsWith',
  range = 'range',
  xDaysGreaterThan = 'xDaysGreaterThan',  // Custom Type for Dates
  xDaysLessThan = 'xDaysLessThan',        // Custom Type for Dates
}

//#endregion

//#region CONST FilterTypesPresence
/**
 * Filter types for checking whether a field value is missing or empty.
 * Available for all column types; no value input is required.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesPresence: readonly IFilterType[] = [
  {enum: FilterTypesEnum.isEmpty, description: 'is empty', inputs: 'none', symbol: ''},
  {enum: FilterTypesEnum.isNotEmpty, description: 'is not empty', inputs: 'none', symbol: ''},
];
//#endregion

//#region CONST FilterTypesString
/**
 * A predefined list of filter types with their metadata.
 * Each filter type includes an associated enum value, description, input type, and symbol representation.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesString: readonly IFilterType[] = [
  {enum: FilterTypesEnum.equalTo, description: 'equal to', inputs: 'single', symbol: '='},
  {enum: FilterTypesEnum.contains, description: 'contains', inputs: 'single', symbol: ':'},
  {enum: FilterTypesEnum.doesNotContain, description: 'does not contain', inputs: 'single', symbol: '!'},
  {enum: FilterTypesEnum.startsWith, description: 'starts with', inputs: 'single', symbol: ':'},
  {enum: FilterTypesEnum.endsWith, description: 'ends with', inputs: 'single', symbol: ':'},
  {enum: FilterTypesEnum.notEqualTo, description: 'not equal to', inputs: 'single', symbol: '!='},
  // {enum: FilterTypesEnum.isOneOf, description: 'is one of', inputs: 'multiple', symbol: '[]'},
  // { enum: FilterTypesEnum.isNotAnyOf, description: 'is not any of', inputs: 'multiple', symbol: '![]' },
];
//#endregion

//#region CONST FilterTypesStringArray
/**
 * Filter types for string-array columns. Uses the same "contains" label with array-specific Typesense handling.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesStringArray: readonly IFilterType[] = [
  {enum: FilterTypesEnum.contains, description: 'contains', inputs: 'single', symbol: ':'},
  {enum: FilterTypesEnum.doesNotContain, description: 'does not contain', inputs: 'single', symbol: '!='},
];
//#endregion

//#region CONST FilterTypesNumber
/**
 * A predefined list of numerical filter types with their metadata.
 * Each filter type includes an associated enum value, description, input type, and symbol representation.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesNumber: readonly IFilterType[] = [
  {enum: FilterTypesEnum.equalTo, description: 'equal to', inputs: 'single', symbol: '='},
  {enum: FilterTypesEnum.notEqualTo, description: 'not equal to', inputs: 'single', symbol: '!='},
  {enum: FilterTypesEnum.greaterThan, description: 'greater than', inputs: 'single', symbol: '>'},
  {enum: FilterTypesEnum.lessThan, description: 'less than', inputs: 'single', symbol: '<'},
  {enum: FilterTypesEnum.lessThanOrEqualTo, description: 'less than or equal to', inputs: 'single', symbol: '<='},
  {enum: FilterTypesEnum.greaterThanOrEqualTo, description: 'greater than or equal to', inputs: 'single', symbol: '>='},
  {enum: FilterTypesEnum.range, description: 'between', inputs: 'multiple', symbol: '[..]'},
  // { enum: FilterTypesEnum.isOneOf, description: 'is one of', inputs: 'multiple', symbol: '[]' },
  // { enum: FilterTypesEnum.isNotAnyOf, description: 'is not any of', inputs: 'multiple', symbol: '![]' },
];
//#endregion

//#region CONST FilterTypesBoolean
/**
 * A predefined list of boolean filter types with their metadata.
 * Each filter type includes an associated enum value, description, input type, and symbol representation.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesBoolean: readonly IFilterType[] = [
  {enum: FilterTypesEnum.equalTo, description: 'equal to', inputs: 'single', symbol: '='},
  {enum: FilterTypesEnum.notEqualTo, description: 'not equal to', inputs: 'single', symbol: '!='},
  // { enum: FilterTypesEnum.isOneOf, description: 'is one of', inputs: 'multiple', symbol: '[]' },
  // { enum: FilterTypesEnum.isNotAnyOf, description: 'is not any of', inputs: 'multiple', symbol: '![]' },
];
//#endregion

//#region CONST FilterTypesDate
/**
 * A predefined list of date filter types.
 * Reuses the number-based filter types as date comparisons follow similar logic.
 *
 * @constant
 * @type {IFilterType[]}
 */
export const FilterTypesDate: readonly IFilterType[] = [
  ...FilterTypesNumber,
  // Custom Types For Dates as Numbers =============================================================================
  {enum: FilterTypesEnum.xDaysGreaterThan, description: 'greater than (x) days', inputs: 'single', symbol: '>'},
  {enum: FilterTypesEnum.xDaysLessThan, description: 'less than (x) days', inputs: 'single', symbol: '<'},
];
//#endregion

//#region ENUM FilterTypeTypesenseEnum
/**
 * Enum for filter types used in Typesense.
 * Represents various comparison operators and their corresponding descriptions for filtering.
 *
 * @readonly
 * @enum {string}
 */
export enum FilterTypeTypesenseEnum {
  EqualTo = 'equal to',
  PartiallyEqualTo = 'partially equal to',
  Contains = 'contains',
  NotEqualTo = 'not equal to',
  GreaterThan = 'greater than',
  LessThan = 'less than',
  GreaterThanOrEqualTo = 'greater than or equal to',
  LessThanOrEqualTo = 'less than or equal to',
  DoesNotContain = 'does not contain',
  StartsWith = 'starts with',
  EndsWith = 'ends with',
  IsEmpty = 'is empty',
  IsNotEmpty = 'is not empty',
  IsOneOf = 'is one of',
  IsNotAnyOf = 'is not any of',
  Between = 'between',
  xDaysGreaterThan = 'greater than (x) days',  // Custom Type for Dates
  xDaysLessThan = 'less than (x) days',        // Custom Type for Dates
}

//#endregion

//#region CONST FilterTypeToSymbol
/**
 * Mapping of filter types to their corresponding symbols.
 * Associates each filter type from the `FilterTypeTypesense` enum with its string symbol representation used for filtering.
 *
 * @constant
 * @type {{ [key in FilterTypeTypesenseEnum]: string }}
 */

export const FilterTypeToSymbol: Readonly<Record<FilterTypeTypesenseEnum, string>> = {
  [FilterTypeTypesenseEnum.EqualTo]: '=',
  [FilterTypeTypesenseEnum.PartiallyEqualTo]: '',
  [FilterTypeTypesenseEnum.Contains]: '',
  [FilterTypeTypesenseEnum.NotEqualTo]: '!=',
  [FilterTypeTypesenseEnum.GreaterThan]: '>',
  [FilterTypeTypesenseEnum.LessThan]: '<',
  [FilterTypeTypesenseEnum.GreaterThanOrEqualTo]: '>=',
  [FilterTypeTypesenseEnum.LessThanOrEqualTo]: '<=',
  [FilterTypeTypesenseEnum.DoesNotContain]: '!',
  [FilterTypeTypesenseEnum.StartsWith]: '',
  [FilterTypeTypesenseEnum.EndsWith]: '',
  [FilterTypeTypesenseEnum.IsEmpty]: '',
  [FilterTypeTypesenseEnum.IsNotEmpty]: '',
  [FilterTypeTypesenseEnum.IsOneOf]: '=',
  [FilterTypeTypesenseEnum.IsNotAnyOf]: '!=',
  [FilterTypeTypesenseEnum.Between]: '..',
  [FilterTypeTypesenseEnum.xDaysGreaterThan]: '>',
  [FilterTypeTypesenseEnum.xDaysLessThan]: '<',
};
//#endregion

//#region ENUM LogicalOperatorEnum
/**
 * Enum for logical operators.
 * Represents logical conjunctions used in filtering expressions.
 *
 * @readonly
 * @enum {string}
 */
export enum LogicalOperatorEnum {
  AND = 'AND',
  OR = 'OR',
}

//#endregion

//#region CONST OperatorMap
/**
 * Mapping of logical operators to their JavaScript equivalent symbols.
 * Associates each logical operator from the `LogicalOperator` enum with its corresponding symbol for logical operations.
 *
 * @constant
 * @type {{ [key in LogicalOperatorEnum]: string }}
 */
export const OperatorMap: Record<LogicalOperatorEnum, string> = {
  [LogicalOperatorEnum.AND]: '&&',
  [LogicalOperatorEnum.OR]: '||',
};
//#endregion
